import { z } from "zod";

import { CrmDealCreationResultSchema } from "./crmDealCreation";
import { MsdynamicsValueMapSchema } from "./dynamicsValueMap";
import { FieldMappingAiConfigSchema, FieldMappingSourceType } from "./hubspot";

export type { AiTestRequest, AiTestResponse, FieldMappingAiConfig } from "./hubspot";
export { AiTestResponseSchema } from "./hubspot";

export * from "./dynamicsDealConfig";
export * from "./dynamicsValueMap";

// Re-export the shared source type enum so Dynamics consumers don't need the hubspot schema.
export { FieldMappingSourceType };

export {
	createDynamicsConnectionSchema,
	isValidDynamicsEnvironmentUrl,
	normalizeDynamicsEnvironmentUrl,
} from "./dynamicsEnvironmentUrl";
export type { DynamicsConnectionFormValues } from "./dynamicsEnvironmentUrl";

/** Dataverse entities the Dynamics 365 sync can write to. */
export enum MsdynamicsEntityType {
	LEAD = "LEAD",
	CONTACT = "CONTACT",
	ACCOUNT = "ACCOUNT",
	OPPORTUNITY = "OPPORTUNITY",
	/** A received reply with a sentiment, as a Dataverse campaign response (PREC-4367). */
	CAMPAIGN_RESPONSE = "CAMPAIGN_RESPONSE",
	/** The campaign a Signalist list becomes (PREC-4368). */
	CAMPAIGN = "CAMPAIGN",
	/** The marketing list a Signalist campaign becomes (PREC-4368). */
	MARKETING_LIST = "MARKETING_LIST",
}

/**
 * Opportunity attributes owned by the deal-config endpoint or by the sync's relationship
 * linking. `POST field-mappings` rejects them with a 400, so they are hidden from the
 * opportunity target-field picker.
 */
export const MSDYNAMICS_OPPORTUNITY_RESERVED_FIELD_KEYS: readonly string[] = [
	"name",
	"processid",
	"stageid",
	"ownerid",
	"estimatedclosedate",
	"parentcontactid",
	"parentaccountid",
	"customerid",
];

// ── Attribute metadata ───────────────────────────────────────────────

/**
 * Dataverse option values are numeric and the API stringifies them
 * (`String(option.Value)`). Coerced rather than `z.string()` so a value that ever arrives
 * as a raw number still lands as the string a `<select>` id must be.
 */
export const MsdynamicsFieldOptionSchema = z.object({
	label: z.string(),
	value: z.coerce.string(),
});

export const MsdynamicsFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	/** Dataverse `AttributeType`: `String` / `Memo` / `Picklist` / `Lookup` / `Money` / … */
	type: z.string(),
	isCustom: z.boolean().optional(),
	description: z.string().optional(),
	options: z.array(MsdynamicsFieldOptionSchema).optional(),
	isRequired: z.boolean().optional(),
	isReadOnly: z.boolean().optional(),
	isCalculated: z.boolean().optional(),
	isMulti: z.boolean().optional(),
	/** A Lookup/Customer/Owner/PartyList attribute: written as an @odata.bind, never a value. */
	isRelationship: z.boolean().optional(),
});

export type MsdynamicsField = z.infer<typeof MsdynamicsFieldSchema>;

export const MsdynamicsFieldsResponseSchema = z.object({
	fields: z.array(MsdynamicsFieldSchema),
	entityType: z.string().optional(),
});

export type MsdynamicsFieldsResponse = z.infer<typeof MsdynamicsFieldsResponseSchema>;

export const MsdynamicsSourceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string().optional(),
	category: z.string().optional(),
	/** The source writes a value map, so it can target a relationship attribute as an @odata.bind. */
	hasValueMap: z.boolean().optional(),
});

export type MsdynamicsSourceField = z.infer<typeof MsdynamicsSourceFieldSchema>;

/**
 * Keyed by Dataverse entity rather than the person/organization split the other CRMs use.
 * `leadFields` carries prospect **and** organization fields because a Dataverse `lead`
 * holds both on one record.
 */
export const MsdynamicsSourceFieldsResponseSchema = z.object({
	contactFields: z.array(MsdynamicsSourceFieldSchema),
	accountFields: z.array(MsdynamicsSourceFieldSchema),
	opportunityFields: z.array(MsdynamicsSourceFieldSchema).optional(),
	leadFields: z.array(MsdynamicsSourceFieldSchema).optional(),
	campaignResponseFields: z.array(MsdynamicsSourceFieldSchema).optional(),
	campaignFields: z.array(MsdynamicsSourceFieldSchema).optional(),
	marketingListFields: z.array(MsdynamicsSourceFieldSchema).optional(),
});

export type MsdynamicsSourceFieldsResponse = z.infer<typeof MsdynamicsSourceFieldsResponseSchema>;

// ── Field mappings ───────────────────────────────────────────────────

/**
 * The stored AI config. The API nulls out unset columns, so each optional field is
 * normalised back to `undefined` — that is the shape the shared `AiConfigModal` consumes.
 */
export const MsdynamicsAiConfigSchema = z.object({
	prompt: z.string(),
	fallbackValue: z
		.string()
		.nullish()
		.transform((value) => value ?? undefined),
	syncReasoningTargetField: z
		.string()
		.nullish()
		.transform((value) => value ?? undefined),
	syncSourceTargetField: z
		.string()
		.nullish()
		.transform((value) => value ?? undefined),
});

export const MsdynamicsFieldMappingSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(MsdynamicsEntityType),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	sourceField: z.string(),
	msdynamicsField: z.string(),
	msdynamicsFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean(),
	aiConfig: MsdynamicsAiConfigSchema.nullish().transform((value) => value ?? undefined),
	valueMap: MsdynamicsValueMapSchema.nullish(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type MsdynamicsFieldMapping = z.infer<typeof MsdynamicsFieldMappingSchema>;

export const MsdynamicsFieldMappingsResponseSchema = z.array(MsdynamicsFieldMappingSchema);

export type MsdynamicsFieldMappingsResponse = z.infer<typeof MsdynamicsFieldMappingsResponseSchema>;

export const CreateMsdynamicsFieldMappingSchema = z.object({
	entityType: z.nativeEnum(MsdynamicsEntityType),
	sourceField: z.string().min(1),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	msdynamicsField: z.string().min(1),
	msdynamicsFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type CreateMsdynamicsFieldMapping = z.infer<typeof CreateMsdynamicsFieldMappingSchema>;

export const UpdateMsdynamicsFieldMappingSchema = z.object({
	sourceField: z.string().optional(),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	msdynamicsField: z.string().optional(),
	msdynamicsFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type UpdateMsdynamicsFieldMapping = z.infer<typeof UpdateMsdynamicsFieldMappingSchema>;

export const BulkCreateMsdynamicsFieldMappingsSchema = z.object({
	mappings: z.array(CreateMsdynamicsFieldMappingSchema),
});

export type BulkCreateMsdynamicsFieldMappings = z.infer<
	typeof BulkCreateMsdynamicsFieldMappingsSchema
>;

// ── Sync triggers ────────────────────────────────────────────────────

export enum MsdynamicsSyncTrigger {
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export enum MsdynamicsSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export const MsdynamicsCompanySyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsSyncTrigger),
});

export type MsdynamicsCompanySyncTriggerResponse = z.infer<
	typeof MsdynamicsCompanySyncTriggerResponseSchema
>;

export const MsdynamicsListSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsSyncTriggerWithInherit),
	effectiveTrigger: z.nativeEnum(MsdynamicsSyncTrigger),
	isInherited: z.boolean(),
});

export type MsdynamicsListSyncTriggerResponse = z.infer<
	typeof MsdynamicsListSyncTriggerResponseSchema
>;

export const UpdateMsdynamicsCompanySyncTriggerSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsSyncTrigger),
});

export type UpdateMsdynamicsCompanySyncTrigger = z.infer<
	typeof UpdateMsdynamicsCompanySyncTriggerSchema
>;

export const UpdateMsdynamicsListSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsSyncTriggerWithInherit),
});

export type UpdateMsdynamicsListSyncTrigger = z.infer<typeof UpdateMsdynamicsListSyncTriggerSchema>;

// ── Sync logs & resync ───────────────────────────────────────────────

export const MsdynamicsSyncLogSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(MsdynamicsEntityType),
	status: z.enum(["PENDING", "IN_PROGRESS", "SUCCESS", "PARTIAL", "FAILED"]),
	recordsSynced: z.coerce.number(),
	recordsFailed: z.coerce.number(),
	errors: z.array(z.record(z.string(), z.unknown())).nullish(),
	startedAt: z.string().nullish(),
	completedAt: z.string().nullish(),
	createdAt: z.string(),
});

export type MsdynamicsSyncLog = z.infer<typeof MsdynamicsSyncLogSchema>;

/** `GET sync/logs` answers a paged envelope, not a bare array (unlike the web Pipedrive schema). */
export const MsdynamicsSyncLogsResponseSchema = z.object({
	logs: z.array(MsdynamicsSyncLogSchema),
	total: z.coerce.number(),
});

export type MsdynamicsSyncLogsResponse = z.infer<typeof MsdynamicsSyncLogsResponseSchema>;

export const MsdynamicsResyncResponseSchema = z.object({
	queued: z.boolean(),
	queuedCount: z.coerce.number(),
	companyId: z.number(),
	triggeredAt: z.string(),
});

export type MsdynamicsResyncResponse = z.infer<typeof MsdynamicsResyncResponseSchema>;

// ── Opportunity creation trigger ─────────────────────────────────────

export enum MsdynamicsDealCreationTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

/**
 * Company-scoped only — `msdynamics_deal_creation_trigger` has a unique `companyId` and
 * no per-list override, so there is no list-level counterpart to render.
 */
export const MsdynamicsDealCreationTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsDealCreationTrigger),
	createAsLead: z.boolean(),
});

export type MsdynamicsDealCreationTriggerResponse = z.infer<
	typeof MsdynamicsDealCreationTriggerResponseSchema
>;

export const UpdateMsdynamicsDealCreationTriggerSchema = z.object({
	trigger: z.nativeEnum(MsdynamicsDealCreationTrigger),
	createAsLead: z.boolean().optional(),
});

export type UpdateMsdynamicsDealCreationTrigger = z.infer<
	typeof UpdateMsdynamicsDealCreationTriggerSchema
>;

// ── User mappings ────────────────────────────────────────────────────

export const MsdynamicsUserMappingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	msdynamicsOwnerId: z.string(),
	msdynamicsOwnerName: z.string().nullish(),
	userFirstName: z.string().nullish(),
	userLastName: z.string().nullish(),
	userEmail: z.string().nullish(),
	/** Owner privileges the Dynamics user lacks; empty when none, null when unchecked (PREC-4361). */
	missingPrivileges: z.array(z.string()).nullish(),
});

export type MsdynamicsUserMapping = z.infer<typeof MsdynamicsUserMappingSchema>;

export const MsdynamicsUserMappingsResponseSchema = z.array(MsdynamicsUserMappingSchema);

export type MsdynamicsUserMappingsResponse = z.infer<typeof MsdynamicsUserMappingsResponseSchema>;

export const CreateMsdynamicsUserMappingSchema = z.object({
	userId: z.number(),
	msdynamicsOwnerId: z.string(),
	msdynamicsOwnerName: z.string().optional(),
});

export type CreateMsdynamicsUserMapping = z.infer<typeof CreateMsdynamicsUserMappingSchema>;

// ── Manual opportunity / lead creation ───────────────────────────────

export const CreateMsdynamicsDealRequestSchema = z.object({
	prospectId: z.number(),
	dealName: z.string().optional(),
	createAsLead: z.boolean().optional(),
	pipeline: z.string().optional(),
	dealStage: z.string().optional(),
	dealOwnerId: z.string().optional(),
	/** Dataverse `estimatedclosedate` is a DateTime, so this is a full ISO 8601 timestamp. */
	closeDate: z.string().optional(),
	estimatedValue: z.number().optional(),
});

export type CreateMsdynamicsDealRequest = z.infer<typeof CreateMsdynamicsDealRequestSchema>;

export const CreateMsdynamicsDealResponseSchema = CrmDealCreationResultSchema;

export type CreateMsdynamicsDealResponse = z.infer<typeof CreateMsdynamicsDealResponseSchema>;

// ── Marketing lists & campaigns ──────────────────────────────────────

/**
 * The two Dataverse marketing switches: a marketing list per Signalist campaign (whose
 * members are the synced contacts), and a Dataverse campaign per Signalist list.
 */
export const MsdynamicsMarketingConfigSchema = z.object({
	autoCreateMarketingLists: z.boolean(),
	autoCreateCampaigns: z.boolean(),
	/** Replies with a sentiment as campaign responses; only while campaigns are created (PREC-4367). */
	syncCampaignResponses: z.boolean().default(false),
	/** Conversation notes also written on the contact's account (PREC-4751). */
	attachConversationNotesToAccount: z.boolean().default(false),
});

export type MsdynamicsMarketingConfig = z.infer<typeof MsdynamicsMarketingConfigSchema>;

/**
 * `PUT marketing-config` always sends the three marketing switches. The account-note switch is
 * sent only by its own checkbox, so a company that never touched it keeps it unset (the API keeps
 * what is stored when it is omitted).
 */
export const UpdateMsdynamicsMarketingConfigSchema = MsdynamicsMarketingConfigSchema.extend({
	attachConversationNotesToAccount: z.boolean().optional(),
});

export type UpdateMsdynamicsMarketingConfig = z.infer<typeof UpdateMsdynamicsMarketingConfigSchema>;

// ── Conversation sync & OAuth ────────────────────────────────────────

export const MsdynamicsConversationSyncResponseSchema = z.object({ enabled: z.boolean() });

export type MsdynamicsConversationSyncResponse = z.infer<
	typeof MsdynamicsConversationSyncResponseSchema
>;

export const UpdateMsdynamicsConversationSyncSchema = z.object({ enabled: z.boolean() });

export type UpdateMsdynamicsConversationSync = z.infer<
	typeof UpdateMsdynamicsConversationSyncSchema
>;

export const MsdynamicsOAuthConnectResponseSchema = z.object({ url: z.string() });

export type MsdynamicsOAuthConnectResponse = z.infer<typeof MsdynamicsOAuthConnectResponseSchema>;

export const MsdynamicsOAuthStatusResponseSchema = z.object({
	connected: z.boolean(),
	/** The Dataverse environment URL the tokens are scoped to. */
	apiDomain: z.string().nullish(),
	uiDomain: z.string().nullish(),
	tenantId: z.string().nullish(),
	msdynamicsUserId: z.string().nullish(),
	msdynamicsOrgId: z.string().nullish(),
	tokenExpiry: z.string().nullish(),
});

export type MsdynamicsOAuthStatusResponse = z.infer<typeof MsdynamicsOAuthStatusResponseSchema>;

export const MsdynamicsOAuthDisconnectResponseSchema = z.object({ success: z.boolean() });

export type MsdynamicsOAuthDisconnectResponse = z.infer<
	typeof MsdynamicsOAuthDisconnectResponseSchema
>;
